import { Box, HStack, Link, Stack } from '@web/components/atoms/Layout'
import { ChakraPopover } from '@web/components/atoms/Popover'
import { Body2 } from '@web/components/atoms/Typography'
import { toDisplayAmount } from '@web/components/utils/format'
import { type Comment as CommentApiProps } from '@web/graphql-components'

export type CommentProps = Omit<CommentApiProps, 'donatedTo'> & {
  showAmount: boolean
  showMessage: boolean
  donatedTo?: { name: string; url: string }
  styles?: { [key in string]: string }
}

const CommentInner = ({
  amount,
  donatedAt,
  donatedBy,
  donatedTo,
  message,
  styles,
  showAmount,
  showMessage,
  dedicateeName,
  organisationName,
}: CommentProps & { styles?: { [key in string]: string } }) => {
  const donorName = organisationName || donatedBy
  return (
    <Stack
      padding="6px 12px"
      backgroundColor={styles?.backgroundColor ?? 'ge.white'}
      border="1px solid transparent"
      borderRadius={`${styles?.borderRadius ? `${styles.borderRadius}px` : '4px'}`}
      className="comment group"
      flex="0 0 auto"
      css={{
        // beating specifity of chakra's default styles
        '&.comment': {
          borderColor: styles?.borderColor ?? 'ge.background.secondary',
        },
      }}
      w={styles?.width ?? '270px'}
      h={styles?.height}
    >
      <Box>
        <HStack justifyContent="space-between">
          {showAmount && (
            <Body2 as="strong" fontWeight={700}>
              ${toDisplayAmount(amount).replace(/.00$/, '')}
            </Body2>
          )}
          <Box margin="0 0 0 auto">
            <Body2 className="comment__date" fontSize="0.875rem" color="ge.text.secondary">
              {donatedAt}
            </Body2>
          </Box>
        </HStack>
        <Body2 {...(styles?.height ? { lineClamp: 1, textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : {})}>
          {dedicateeName ? (
            <>
              Dedicated to {dedicateeName} from {donorName}
            </>
          ) : (
            <>{donorName} donated </>
          )}
          {donatedTo && (
            <Link
              display="inline"
              href={donatedTo.url}
              color={styles?.fundraiserLinkColor ?? 'var(--branding-color-brand)'}
              target="_blank"
              _groupHover={{
                textDecoration: 'underline',
              }}
            >
              to {donatedTo.name}
            </Link>
          )}
        </Body2>
      </Box>
      {showMessage && (
        <Body2 className="comment__message" lineClamp={styles?.height ? 2 : undefined} color="ge.text.secondary">
          {message}
        </Body2>
      )}
    </Stack>
  )
}

const Comment = (props: CommentProps & { styles?: { [key in string]: string }; withPopover?: boolean }) => {
  const canShowMessage = props.showMessage && props.message
  return (
    <ChakraPopover.Root
      positioning={{
        overlap: true,
        sameWidth: true,
      }}
      lazyMount={true}
      open={props.withPopover && canShowMessage ? undefined : false}
    >
      <ChakraPopover.Trigger asChild {...(canShowMessage && props.withPopover ? { cursor: 'pointer' } : {})}>
        <Box w={props.styles?.width ?? '270px'}>
          <CommentInner {...props} />
        </Box>
      </ChakraPopover.Trigger>
      <ChakraPopover.Positioner>
        <ChakraPopover.Context>
          {(state) => (
            <ChakraPopover.Content onClick={() => state.setOpen(false)}>
              <ChakraPopover.Body overflowY="auto" p={0}>
                <CommentInner {...props} styles={{ ...props.styles, height: '' }} />
              </ChakraPopover.Body>
            </ChakraPopover.Content>
          )}
        </ChakraPopover.Context>
      </ChakraPopover.Positioner>
    </ChakraPopover.Root>
  )
}

export default Comment
